import { motion } from "framer-motion";
import {
  Users,
  FileText,
  Sheet as SheetIcon,
  FolderOpen,
  MessageCircle,
  CreditCard,
  BarChart3,
} from "lucide-react";
import ConvergenceVisual from "@/components/landing/ConvergenceVisual";
import { useSiteContent } from "@/context/ContentContext";

const ICONS = [Users, FileText, SheetIcon, FolderOpen, MessageCircle, CreditCard, BarChart3];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.05, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export default function ProblemSection() {
  const { problem } = useSiteContent();
  const tools = problem.tools || [];
  return (
    <section
      id="problema"
      className="relative scroll-mt-20 border-t border-black/[0.08] py-16 md:py-24 lg:py-28"
    >
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-emerald-700">
            {problem.eyebrow}
          </span>
          <h2
            data-testid="problem-headline"
            className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-3xl lg:text-4xl"
          >
            {problem.headline}
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <p className="mb-5 text-sm text-tertiary">{problem.intro}</p>
            <div data-testid="problem-tool-list" className="space-y-2.5">
              {tools.map((t, i) => {
                const Icon = ICONS[i % ICONS.length];
                return (
                  <motion.div
                    key={`${t.task}-${i}`}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-40px" }}
                    variants={fadeUp}
                    custom={i}
                    className="flex items-center justify-between rounded-xl border border-black/10 bg-white px-4 py-3.5 transition-colors duration-200 hover:border-black/15"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-black/10 bg-white text-secondary">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="text-[15px] font-medium text-[color:var(--text-primary)]">
                        {t.task}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-tertiary">
                      <span className="text-black/30">{"\u2192"}</span>
                      <span className="font-mono-tech text-xs">{t.tool}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div id="approccio" className="scroll-mt-20 lg:col-span-6">
            <ConvergenceVisual />
          </div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="mt-14 max-w-3xl border-l-2 border-emerald-400/60 pl-5"
        >
          <p
            data-testid="problem-closing-copy"
            className="text-lg leading-relaxed text-[color:var(--text-primary)] md:text-xl"
          >
            {problem.closing}
          </p>
          <p className="mt-4 font-mono-tech text-xs uppercase tracking-[0.16em] text-emerald-700">
            {problem.tagline}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
